'use client';

import React from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { formatDate } from '@/helpers/date';
import { useOptimizedImage } from '@/hooks/useOptimizedImage';
import { getRecommendedImageProps, getContextDimensions } from '@/utils/imageOptimization';
import { IMAGE_CONTEXTS } from '@/constants/imageConfig';
import { BlogPost } from '@/helpers/content/queries/types';
import styles from './BlogCard.module.css';

interface BlogCardProps {
  post: BlogPost;
  locale: string;
  readMoreText?: string;
}

export const BlogCard: React.FC<BlogCardProps> = ({ post, locale, readMoreText = "Read More" }) => {
  const optimizedImageProps = post.featuredImage ? useOptimizedImage(
    post.featuredImage.url,
    post.featuredImage.title || post.title,
    post.featuredImage.width || 400,
    post.featuredImage.height || 225,
    IMAGE_CONTEXTS.BLOG_CARD,
    true // priority for blog cards (often above the fold)
  ) : null;

  // Get recommended Next.js props for this context
  const recommendedProps = getRecommendedImageProps(IMAGE_CONTEXTS.BLOG_CARD);
  const contextDimensions = getContextDimensions(IMAGE_CONTEXTS.BLOG_CARD);

  return (
    <div className={styles.card}>
      {optimizedImageProps && (
        <Link href={`/${locale}/blog/${post.slug}`} className={styles.imageContainer}>
          <Image
            // Use optimized props for non-conflicting features
            src={optimizedImageProps.src}
            alt={optimizedImageProps.alt}
            priority={optimizedImageProps.priority}
            placeholder={optimizedImageProps.placeholder}
            blurDataURL={optimizedImageProps.blurDataURL}
            // Use recommended Next.js props for optimization
            width={contextDimensions.width}
            height={contextDimensions.height}
            sizes={recommendedProps.sizes}
            quality={recommendedProps.quality}
            className={styles.image}
          />
        </Link>
      )}
      <div className={styles.cardContent}>
        {/* Publish Date and Categories Row */}
        <div className={styles.metaRow}>
          <div className={styles.publishDate}>
            {formatDate(post.sys.publishedAt, locale)}
          </div>
          {post.categories?.items && post.categories.items.length > 0 && (
            <div className={styles.categoryContainer}>
              {post.categories.items.map((category) => (
                <Link 
                  key={category.slug}
                  href={`/${locale}/blog?category=${category.slug}`}
                  className={styles.categoryTag}
                >
                  {category.name}
                </Link>
              ))}
            </div>
          )}
        </div>
        <Link href={`/${locale}/blog/${post.slug}`} className={styles.title}>
          {post.title}
        </Link>
        <p className={styles.excerpt}>
          {post.excerpt}
        </p>
        <Link
          href={`/${locale}/blog/${post.slug}`}
          className={styles.readMore}
        >
          {readMoreText}
        </Link>
      </div>
    </div>
  );
}; 